import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { usePageContext } from "vike-react/usePageContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { resolveChipRouteType } from "@/components/Chat/suggestions";
import {
  chatClientErrorMap,
  clearStoredSession,
  fetchThread,
  fetchUnread,
  isChatClientFailure,
  mintOrRefreshSession,
  openStream,
  readStoredSessionId,
  requestAbort,
  writeStoredSessionId,
  RECONNECT_INITIAL_DELAY_MS,
  RECONNECT_MAX_ATTEMPTS,
  RECONNECT_MAX_DELAY_MS,
  type ChatClientErrorCode,
  type ChatClientResult,
  type PageContext,
  type ThreadTurn,
  type WireFrame,
} from "@/api/chatStreamClient";
import {
  createMessageKeyRegistry,
  identityTransitionMap,
  resolveIdentityTransition,
  resolveUnreadDot,
  shouldShowReconnectIndicator,
} from "@/lib/chatTurnPolicy";
import { mergeThreadRows, resolveThreadPollDelayMs } from "@/lib/chatThreadPoll";

/**
 * The chat panel's brain: session identity, thread rehydration, unread detection, the identity
 * transitions, and every PostHog capture the panel makes.
 *
 * Three structural choices, each with a consequence if reversed:
 *
 *  1. **The state lives in a MODULE-LEVEL store the provider subscribes to**, not in component
 *     state. A Vike client-side navigation may remount the layout, and this provider with it; an
 *     in-flight stream held in `useState` would die on every navigation. D-13 and D-14 explicitly
 *     contemplate asking "الأسعار هنا كويسة؟" and then navigating, so surviving a remount is a
 *     requirement rather than a precaution. The store is safe either way, which is why it is not
 *     conditional on the remount question's answer.
 *  2. **Every capture lives here, never in a component.** The panel's CTAs appear in several places
 *     (empty-state chips, the composer, the limit row) and CLAUDE.md's shared-primitive rule puts the
 *     capture in the shared handler. It is also the only way the components stay dumb.
 *  3. **The rules are DELEGATED to `@/lib/chatTurnPolicy`, not re-derived here.** The reconnect
 *     visibility threshold, the message-key lifecycle, the identity transitions and the unread gate
 *     are pure decisions with a node-environment suite behind them. A second copy in this file would
 *     be a second thing to keep in step with that suite, and its drift would be silent.
 */

// ─── Public shapes ────────────────────────────────────────────────────────────

export type ChatRouteType = "store" | "category" | "generic";

/** One conversational turn as the panel renders it. */
export type ChatTurnRow = {
  kind: "turn";
  id: string;
  role: "user" | "assistant";
  content: string;
  /** `interrupted` peels its marker line off into a footer (38-UI-SPEC § Interrupted turn). */
  intent?: string;
  /**
   * Phase 37's block envelope. Phase 39 fills AND renders `stores` (the store carousel, WEB-03);
   * `cta` remains unfilled after the 2026-08-29 rescope, so it still degrades to its own `text`.
   */
  blocks?: unknown;
  createdAt: string;
  /** Optimistic user turn, rendered at reduced opacity until the first server frame. */
  pending?: boolean;
  /** Still growing. The caret trails this bubble and nothing else. */
  streaming?: boolean;
  /** D-17: a deep tool was actually refused, so this bubble discloses the lighter mode. */
  degraded?: boolean;
};

/**
 * A row that is NOT the bot talking. A rate refusal or a transport failure dressed in the bot's
 * voice is the dishonesty the phase's tone target rejects, so these render as system rows.
 */
export type ChatSystemRow = {
  kind: "system";
  id: string;
  system: "limit" | "error" | "stop_failed" | "send_failed";
  createdAt: string;
  layer?: "ip" | "session" | "user";
  retryAfterSec?: number;
  /** A machine code from `chatClientErrorMap`. The Arabic line is `chatCopy.ts`'s job (38-14). */
  code?: ChatClientErrorCode;
};

export type ChatRow = ChatTurnRow | ChatSystemRow;

type ChatState = {
  /**
   * In memory ONLY, never persisted: a persisted "was open" would pop the panel on every page a
   * visitor ever loads, including the ones they never asked ناصح about.
   *
   * IT IS MODULE-LEVEL, AND THAT IS WHAT MAKES D-07 WORK. This field lives in the `let state`
   * declared under "The module-level store" below, which the provider reads through `getSnapshot`
   * rather than seeding into a `useState`. A Vike client-side navigation re-renders the route tree
   * and may remount this provider, but it does not reload the document, so the module instance and
   * therefore `open` are untouched and a provider that mounts a second time reads the SAME snapshot.
   * That is precisely what D-07 needs when a store-card tap moves the visitor to `/store/{slug}`:
   * the panel they were reading is still open when they land.
   *
   * THE PROHIBITION, NAMED HERE SO NOBODY "FIXES" IT LATER: it must NOT be written to browser
   * storage of any kind, neither the durable per-origin flavour nor the per-tab one. (Their API
   * names are deliberately not written out here: the gate on this rule counts storage identifiers in
   * this file, and a warning that trips the very check it exists to protect is a bad warning.)
   * Module scope survives the navigation D-07 cares about and dies with the tab, which is exactly
   * the lifetime this bit should have.
   */
  open: boolean;
  sessionId: string | null;
  conversationId: string | null;
  rows: ChatRow[];
  /** The rehydration fetch is in flight. The list renders skeleton rows, not a spinner (D-03). */
  threadLoading: boolean;
  unreadCount: number | null;
  lastSeenResolved: boolean;

  // ─── The turn in flight ───
  /** A run is live. The composer is locked and the stop control occupies the send slot. */
  streaming: boolean;
  /** The single tool-status line. ONE at a time, replaced rather than appended (D-07). */
  toolName: string | null;
  /** D-09's indicator, decided by `shouldShowReconnectIndicator` and by nothing in this file. */
  reconnecting: boolean;
  /** The abort POST is in flight. The button holds a spinner; the turn keeps streaming. */
  stopPending: boolean;
  /** The active rate refusal, or `null`. Same shape as the 429 body and the `limit` frame. */
  limit: { layer: "ip" | "session" | "user"; retryAfterSec: number } | null;
  /**
   * D-09 and D-11 together: the model-emitted follow-up chips for the LATEST assistant turn ONLY,
   * held in memory and never persisted, so a rehydrated thread carries none.
   *
   * THEY ARE DELIBERATELY NOT A `ChatTurnRow` FIELD. `loadThread()` would then have to invent a
   * value for every rehydrated turn, and a stale chip sitting in scrollback would offer a follow-up
   * to a conversation that has already moved on. Panel state is the right scope: there is exactly
   * one live answer at a time and exactly one chip row to go under it.
   *
   * The empty array is correct here rather than `null`. Unlike `blocks` on the wire — where absent
   * and empty are different facts about what the server sent — this is panel state with a natural
   * "none" value, and every consumer wants to map over it rather than narrow it first.
   */
  suggestedReplies: string[];
  /** Seconds left on the refusal. Reaches 0, then the lock clears with no announcement. */
  countdownSec: number;
  /** Bumped on the visitor's OWN send, which is the one moment the list pins unconditionally. */
  pinToBottomToken: number;
  /**
   * A human (admin / Slack takeover) owns the conversation, as last reported by `GET /thread`. Drives
   * the open panel's poll cadence (5s vs the 20s probe, `chatThreadPoll.ts`). Absent on the wire
   * (an older chat dyno) reads as `false`.
   */
  humanMode: boolean;
};

const INITIAL_STATE: ChatState = {
  open: false,
  sessionId: null,
  conversationId: null,
  rows: [],
  threadLoading: false,
  unreadCount: null,
  lastSeenResolved: false,
  streaming: false,
  toolName: null,
  reconnecting: false,
  stopPending: false,
  limit: null,
  suggestedReplies: [],
  countdownSec: 0,
  pinToBottomToken: 0,
  humanMode: false,
};

// ─── Storage: exactly one key, and it is not the session's ────────────────────

/**
 * The last-seen high-water mark for D-04's dot.
 *
 * The SESSION id is deliberately NOT stored here: `chatStreamClient.ts` already owns that key and
 * already guards it, and its resume path writes a renewed token itself. A second module writing the
 * same value under its own name is how two divergent keys for one concept appear, which this codebase
 * already has one example of and does not need a second.
 */
const LAST_SEEN_STORAGE_KEY = "yetfarrag_chat_last_seen";

/** How many turns a rehydration asks for. The server clamps its own bound as well (38-08). */
const THREAD_LIMIT = 50;

const readLastSeen = (): string | null => {
  if (typeof window !== "undefined") {
    try {
      const raw = window.localStorage.getItem(LAST_SEEN_STORAGE_KEY);
      return typeof raw === "string" && raw.length > 0 ? raw : null;
    } catch {
      // Private-browsing profiles throw on ACCESS, not just on write. No mark means the count is
      // taken from the whole thread, which over-counts in the safe direction.
      return null;
    }
  }
  return null;
};

const writeLastSeen = (value: string): void => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LAST_SEEN_STORAGE_KEY, value);
    } catch {
      // The panel works without persistence; the dot simply reappears next load.
    }
  }
};

const clearLastSeen = (): void => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(LAST_SEEN_STORAGE_KEY);
    } catch {
      // As above.
    }
  }
};

// ─── Route context (D-13) ─────────────────────────────────────────────────────

/**
 * DELEGATES to `Chat/suggestions.ts`, which owns the rule.
 *
 * The three branches used to live here. Plan 38-14 needs the same mapping in the chip module (the
 * chip set is selected by route type) and that module is import-free and covered by a node-environment
 * suite, so it is the better owner: the rule is now stated once, tested once, and a route added to one
 * consumer cannot silently disagree with the other. Two implementations of one routing rule is exactly
 * how the chips a visitor sees and the page context sent to the model drift apart.
 */
export const resolveRouteType = (pathname: string): ChatRouteType => resolveChipRouteType(pathname);

/**
 * D-13's page context: route type plus a slug, and deliberately nothing else. No page-content
 * scraping, no store record fetched to enrich it. The slug is taken verbatim from the URL because
 * that is precisely what the visitor's "هنا" refers to; the server sanitizes and clamps it (38-09).
 */
export const resolvePageContext = (pathname: string): PageContext => {
  const routeType = resolveRouteType(pathname);
  const segments = (typeof pathname === "string" ? pathname : "")
    .split("/")
    .filter((segment) => segment.length > 0);

  if (routeType === "store" && segments.length >= 2) {
    return { routeType, storeSlug: segments[1] };
  }
  if (routeType === "category" && segments.length >= 2) {
    return { routeType, categorySlug: segments[1] };
  }
  return { routeType };
};

// ─── The module-level store ───────────────────────────────────────────────────

type Listener = () => void;

let state: ChatState = INITIAL_STATE;
const listeners = new Set<Listener>();

const getSnapshot = (): ChatState => state;

/**
 * The server render's snapshot. A separate function returning the SAME frozen initial object, so the
 * first client render matches the server HTML exactly and hydration cannot mismatch. (The panel also
 * sits behind `+Layout.tsx`'s post-mount gate, which is belt; this is braces, and it is what makes
 * the module importable by the SSR build at all.)
 */
const getServerSnapshot = (): ChatState => INITIAL_STATE;

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const setState = (patch: Partial<ChatState>): void => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A render bug in one subscriber must not stop the others from being told.
    }
  });
};

/** Test/HMR seam. Never called on a production path. */
export const resetChatStoreForTests = (): void => {
  state = INITIAL_STATE;
  listeners.forEach((listener) => listener());
};

// ─── The runtime the store's actions run in ───────────────────────────────────

type ChatTrackers = ReturnType<typeof usePostHogTracking>["trackChat"];

type ChatRuntimeDeps = {
  track: ChatTrackers;
  /** The site JWT, from `useAuth().session.token` — React state, never a storage read. */
  jwt: string | null;
  routeType: ChatRouteType;
  pathname: string;
};

/** What a send needs in order to be re-issued verbatim on a retry. */
type PendingSend = { text: string; pageContext: PageContext; userRowId: string };

const runtime = {
  deps: null as ChatRuntimeDeps | null,
  /** The raw high-water mark. Nothing renders it, so it stays out of the store. */
  lastSeen: null as string | null,
  /** In-flight mint, so a double open (or StrictMode's double effect) does not mint twice. */
  minting: null as Promise<string | null> | null,
  /** Every timer this runtime owns, so teardown can clear the whole set rather than remembering. */
  timers: new Set<ReturnType<typeof setTimeout>>(),

  // ─── Turn-scoped ───
  /** The live turn's abort handle. Cancels the READER only; stopping the RUN is a POST. */
  controller: null as AbortController | null,
  /** The assistant row the deltas are landing in, created on the first delta. */
  streamingRowId: null as string | null,
  /** `performance`-free elapsed marks. Used for `ttfb_ms` and for D-09's gap. */
  turnStartedAt: 0,
  lastFrameAt: 0,
  firstDeltaSeen: false,
  /** D-17: a deep tool was refused during this run, so the finished bubble discloses it. */
  degradedSeen: false,
  /** An `error` frame's code, surfaced only once the transport has stopped trying. */
  pendingErrorCode: null as ChatClientErrorCode | null,
  /** 1 while the original stream is open; 2+ once it has dropped and is being resumed. */
  attempt: 1,
  /** The send being served, kept so a retry re-issues the same text under the same key. */
  pending: null as PendingSend | null,

  // ─── Thread poll (quick-261021-jzd) ───
  /** A poll fetch is in flight, so a slow response never stacks a second one. */
  pollInFlight: false,
  /**
   * Bumped on turn start, on every full rehydration and on teardown. A poll that started under an
   * older epoch drops its result, so it can never overwrite rows a stream or a reload just wrote.
   */
  threadEpoch: 0,
};

/**
 * ONE registry for the whole panel, holding the client half of exactly-once.
 *
 * The generator is injected here and read nowhere else, which is what keeps `chatTurnPolicy.ts`
 * clock-free and randomness-free. The fallback exists because `randomUUID` needs a secure context
 * and this module must never throw: a visitor on an insecure origin gets a slightly weaker id, not a
 * dead panel. Uniqueness is all the server asks of it.
 */
const messageKeys = createMessageKeyRegistry({
  generate: () =>
    typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`,
});

/** How often the reconnect indicator's condition is re-asked while a stream is down. */
const RECONNECT_WATCH_INTERVAL_MS = 250;

/** How often the rate-limit countdown ticks. */
const COUNTDOWN_INTERVAL_MS = 1000;

const registerTimer = (timer: ReturnType<typeof setTimeout>): void => {
  runtime.timers.add(timer);
};

const clearRuntimeTimers = (): void => {
  runtime.timers.forEach((timer) => clearTimeout(timer));
  runtime.timers.clear();
};

const nowIso = (): string => new Date().toISOString();

let localRowSeq = 0;
const localRowId = (prefix: string): string => {
  localRowSeq += 1;
  return `${prefix}-${localRowSeq}`;
};

const appendRow = (row: ChatRow): void => {
  setState({ rows: [...state.rows, row] });
};

const patchRow = (id: string, patch: Partial<ChatTurnRow>): void => {
  setState({
    rows: state.rows.map((row) =>
      row.kind === "turn" && row.id === id ? { ...row, ...patch } : row
    ),
  });
};

// ─── Session ──────────────────────────────────────────────────────────────────

const toTurnRow = (turn: ThreadTurn): ChatTurnRow => ({
  kind: "turn",
  id: turn.id,
  role: turn.role === "user" ? "user" : "assistant",
  content: turn.content,
  ...(turn.intent === undefined ? {} : { intent: turn.intent }),
  ...(turn.blocks === undefined ? {} : { blocks: turn.blocks }),
  createdAt: turn.createdAt,
});

/**
 * Get a session, minting one only when something actually needs it.
 *
 * Deliberately NOT called on page load. `POST /session` creates a Conversation document, so minting
 * for every visitor who merely loaded a page would write one row per browser per landing — the cost
 * of a dot that is almost always absent. A visitor with no stored token has no thread and therefore
 * nothing unread, which is exactly what the unread path assumes below.
 */
const ensureSession = async (): Promise<string | null> => {
  if (state.sessionId !== null) return state.sessionId;
  if (runtime.minting !== null) return await runtime.minting;

  const attempt = (async (): Promise<string | null> => {
    const result = await mintOrRefreshSession({
      sessionId: readStoredSessionId(),
      jwt: runtime.deps?.jwt ?? null,
    });
    if (isChatClientFailure(result)) return null;
    writeStoredSessionId(result.data.sessionId);
    setState({ sessionId: result.data.sessionId, conversationId: result.data.conversationId });
    return result.data.sessionId;
  })();

  runtime.minting = attempt;
  try {
    return await attempt;
  } finally {
    runtime.minting = null;
  }
};

/**
 * `session_expired` / `session_unknown` is NOT an error the visitor should ever read: it means the
 * token we were holding is dead, and `POST /session` mints on a stale token rather than refusing
 * (38-08). So the whole recovery is "get a new one and ask again", exactly once — a second retry on
 * a fresh token would be chasing a server fault, not a stale credential.
 */
const withSessionRetry = async <T,>(
  call: (sessionId: string) => Promise<ChatClientResult<T>>
): Promise<ChatClientResult<T> | null> => {
  const sessionId = await ensureSession();
  if (sessionId === null) return null;

  const first = await call(sessionId);
  if (!isChatClientFailure(first)) return first;
  if (
    first.code !== chatClientErrorMap.session_expired &&
    first.code !== chatClientErrorMap.session_unknown &&
    first.code !== chatClientErrorMap.session_required
  ) {
    return first;
  }

  clearStoredSession();
  setState({ sessionId: null, conversationId: null });
  const renewed = await ensureSession();
  if (renewed === null) return null;
  return await call(renewed);
};

// ─── Thread rehydration (D-03) ────────────────────────────────────────────────

/**
 * Read the whole thread from the server. An EMPTY result is the empty state (greeting plus chips),
 * not an error: a visitor who has never asked anything has an empty thread, and that is the normal
 * first-open case rather than a failure.
 */
const loadThread = async (): Promise<void> => {
  runtime.threadEpoch += 1;
  setState({ threadLoading: true });
  try {
    const result = await withSessionRetry((sessionId) =>
      fetchThread({ sessionId, jwt: runtime.deps?.jwt ?? null, limit: THREAD_LIMIT })
    );

    if (result === null || isChatClientFailure(result)) {
      // A failed rehydration leaves whatever is already on screen. Replacing a readable thread with
      // an error row would destroy content the visitor can still use.
      return;
    }

    setState({
      conversationId: result.data.conversationId,
      rows: result.data.turns.map(toTurnRow),
      humanMode: result.data.humanMode === true,
    });
    markThreadSeen();
  } finally {
    setState({ threadLoading: false });
  }
};

/**
 * The open panel's silent re-read (quick-261021-jzd): how admin / Slack takeover replies after the
 * first one reach the visitor without a refresh. Never mints a session and never retries on a dead
 * one (that is the next user action's problem), and a failure of any kind (429, network) is
 * swallowed: a poll must never add a system row. The cadence lives in the provider's effect.
 */
const pollThread = async (): Promise<void> => {
  if (runtime.pollInFlight || state.streaming || !state.open || state.sessionId === null) return;
  const epoch = runtime.threadEpoch;
  const conversationId = state.conversationId;
  runtime.pollInFlight = true;
  try {
    const result = await fetchThread({
      sessionId: state.sessionId,
      jwt: runtime.deps?.jwt ?? null,
      limit: THREAD_LIMIT,
    });
    if (isChatClientFailure(result)) return;
    if (
      epoch !== runtime.threadEpoch ||
      state.streaming ||
      result.data.conversationId !== conversationId
    ) {
      return;
    }

    const humanMode = result.data.humanMode === true;
    if (humanMode !== state.humanMode) setState({ humanMode });

    const rows = mergeThreadRows<ChatRow>({
      current: state.rows,
      server: result.data.turns.map(toTurnRow),
    });
    if (rows !== state.rows) {
      // No `pinToBottomToken` bump: that is reserved for the visitor's own send; the list keeps its
      // existing follow-the-bottom behaviour.
      setState({ rows });
      markThreadSeen();
    }
  } catch {
    // Silent by design, see above.
  } finally {
    runtime.pollInFlight = false;
  }
};

/**
 * Write the last-seen mark. On OPEN, not on close: a visitor who opens the panel and immediately
 * closes it has still seen what was in it, and moving this to the close handler would keep the dot
 * lit for someone who just looked.
 */
const markThreadSeen = (): void => {
  const stamp = nowIso();
  runtime.lastSeen = stamp;
  writeLastSeen(stamp);
  setState({ unreadCount: 0 });
};

// ─── Unread (D-04) ────────────────────────────────────────────────────────────

/**
 * The page-load unread check: one cheap count when the page loads, which is what makes a WEB-06
 * completion that landed while the panel was closed findable. No push machinery. The only polling
 * is the OPEN panel's thread re-read (`pollThread`: 5s in human mode, 20s otherwise); a closed
 * panel still relies on this single count.
 */
const refreshUnread = async (): Promise<void> => {
  const stored = readStoredSessionId();
  if (stored === null) {
    // No token means no thread, so the count is zero WITHOUT minting a session to prove it.
    setState({ unreadCount: 0 });
    return;
  }

  const result = await fetchUnread({
    sessionId: stored,
    jwt: runtime.deps?.jwt ?? null,
    since: runtime.lastSeen,
  });

  if (isChatClientFailure(result)) {
    // An unresolved count keeps the launcher dot-free, which is the right failure: a dot the visitor
    // cannot explain is worse than a notice that waits for the next page load.
    return;
  }
  setState({ sessionId: stored, unreadCount: result.data.unread });
};

// ─── Panel open / close ───────────────────────────────────────────────────────

const openPanel = (): void => {
  if (state.open) return;
  const dot = resolveUnreadDot({
    unreadCount: state.unreadCount,
    lastSeenResolved: state.lastSeenResolved,
  });

  setState({ open: true });
  runtime.deps?.track.panelOpened({
    surface: runtime.deps.routeType,
    unread: dot === true,
  });
  void loadThread();
};

const closePanel = (): void => {
  if (!state.open) return;
  setState({ open: false });
  runtime.deps?.track.panelClosed({
    messages_in_thread: state.rows.filter((row) => row.kind === "turn").length,
  });
  // Closing the panel does NOT cancel a run (38-UI-SPEC § Key handling). The turn completes, the
  // assistant turn persists, and D-04's dot surfaces it. Cancellation has exactly one trigger.
};

// ─── Identity transitions (D-11, D-12) ────────────────────────────────────────

/**
 * D-11. Forward the JWT to `POST /session` so the SERVER stamps `userId` on the conversation that is
 * running right now. The client never asserts an identity; it hands over a token the server verifies
 * (T-38-77). The write is idempotent, so firing on any token change is safe.
 *
 * The thread continues as-is: no reload, no turn splicing, no visual interruption, and in particular
 * no abort of an in-flight stream. The one case that does refetch is a server that answers with a
 * DIFFERENT conversation id — arguing with that would leave the panel rendering a thread it can no
 * longer write to.
 */
const attributeIdentity = async (): Promise<void> => {
  const stored = state.sessionId ?? readStoredSessionId();
  if (stored === null) return; // Nothing running and nothing stored: the next mint carries the JWT.

  const result = await mintOrRefreshSession({
    sessionId: stored,
    jwt: runtime.deps?.jwt ?? null,
  });
  if (isChatClientFailure(result)) return;

  writeStoredSessionId(result.data.sessionId);
  const conversationChanged =
    state.conversationId !== null && state.conversationId !== result.data.conversationId;
  setState({ sessionId: result.data.sessionId, conversationId: result.data.conversationId });
  if (conversationChanged && state.open) void loadThread();
};

/**
 * D-12, and it must be unmistakable: this is the shared-computer privacy behaviour (T-38-76).
 *
 * Logging out drops the stored session token, the last-seen mark AND the local thread, so the next
 * use mints a fresh anonymous session. It also tears the runtime down — a stream still writing into
 * a thread we just dropped would render the previous account's answer to whoever is sitting there
 * now, which is the exact disclosure this transition exists to prevent.
 */
const resetIdentity = (): void => {
  teardownChatRuntime();
  clearStoredSession();
  clearLastSeen();
  runtime.lastSeen = null;
  state = { ...INITIAL_STATE, lastSeenResolved: true, unreadCount: 0, open: state.open };
  setState({});
};

// ─── The turn state machine (WEB-01, WEB-07) ──────────────────────────────────

/**
 * Re-ask D-09 whether the visitor should be told anything, and keep re-asking while the stream is
 * down, because the answer changes with the passage of time and not only with an event.
 *
 * The THRESHOLDS are not here. `shouldShowReconnectIndicator` owns them and a node-environment suite
 * pins them; this function's whole job is to feed it a current attempt and a current gap. Evaluating
 * only at each retry's start would light the indicator half a second late, which is why there is a
 * ticker rather than a single check.
 */
const evaluateReconnectVisibility = (): void => {
  const visible = shouldShowReconnectIndicator({
    attempt: runtime.attempt,
    gapMs: Date.now() - runtime.lastFrameAt,
    turnInFlight: state.streaming,
  });
  if (visible !== state.reconnecting) setState({ reconnecting: visible });
};

const scheduleReconnectWatch = (): void => {
  if (!state.streaming) return;
  evaluateReconnectVisibility();
  const timer = setTimeout(() => {
    runtime.timers.delete(timer);
    scheduleReconnectWatch();
  }, RECONNECT_WATCH_INTERVAL_MS);
  registerTimer(timer);
};

/** The bot's growing bubble, created lazily so the "waiting for the first token" state can be dots. */
const ensureStreamingRow = (): string => {
  if (runtime.streamingRowId !== null) return runtime.streamingRowId;
  const id = localRowId("streaming");
  runtime.streamingRowId = id;
  appendRow({
    kind: "turn",
    id,
    role: "assistant",
    content: "",
    createdAt: nowIso(),
    streaming: true,
    ...(runtime.degradedSeen ? { degraded: true } : {}),
  });
  return id;
};

const appendDelta = (text: string): void => {
  const id = ensureStreamingRow();
  const current = state.rows.find((row) => row.kind === "turn" && row.id === id);
  const previous = current !== undefined && current.kind === "turn" ? current.content : "";
  patchRow(id, { content: previous + text });
};

/** The partial answer as it stands. Its LENGTH is the only thing analytics ever sees. */
const currentPartial = (): string => {
  if (runtime.streamingRowId === null) return "";
  const row = state.rows.find(
    (candidate) => candidate.kind === "turn" && candidate.id === runtime.streamingRowId
  );
  return row !== undefined && row.kind === "turn" ? row.content : "";
};

/**
 * Finalise the turn IN PLACE. Same row id, same position in the list, so the bubble the visitor was
 * reading does not re-render into a new node, does not reflow and does not move (D-08, state 6). The
 * server's `content` is authoritative — it carries the interrupted marker as its last line.
 */
const finaliseTurn = ({
  content,
  intent,
  blocks,
}: {
  content: string;
  intent?: string;
  blocks?: unknown;
}): void => {
  const id = ensureStreamingRow();
  patchRow(id, {
    content,
    streaming: false,
    ...(intent === undefined ? {} : { intent }),
    ...(blocks === undefined ? {} : { blocks }),
    ...(runtime.degradedSeen ? { degraded: true } : {}),
  });
  noteStoreCarouselIfPresent(blocks);
};

/**
 * The store carousel's impression (WEB-03), captured HERE and not in the component.
 *
 * Two things this is deliberately not. It is NOT an `IntersectionObserver` in `ChatStoreCarousel`:
 * the event map's rule for this panel is that every capture lives in this file's own handlers, and
 * "the server sent a carousel" is the fact the funnel needs — it is the denominator that makes
 * `chat_store_card_clicked` a rate rather than a raw count. And it is NOT keyed off the RENDER: a
 * block whose every item `toSafeCards` drops still arrived, and losing that from the funnel would
 * hide exactly the failure worth seeing.
 *
 * `blocks` is typed `unknown` because the wire shape is narrowed at the renderer, so every step
 * below is guarded: a non-array, a member that is not an object, a missing `kind` and a non-array
 * `items` all resolve to "no carousel" or to a count of zero rather than to a throw.
 */
const noteStoreCarouselIfPresent = (blocks: unknown): void => {
  if (!Array.isArray(blocks)) return;

  const storesBlock = blocks.find(
    (block) =>
      block !== null &&
      typeof block === "object" &&
      (block as { kind?: unknown }).kind === "stores"
  );
  if (storesBlock === undefined) return;

  const items = (storesBlock as { items?: unknown }).items;
  const cardCount = Array.isArray(items) ? items.length : 0;

  runtime.deps?.track.storeCarouselShown({
    card_count: cardCount,
    route_type: runtime.deps.routeType,
  });
};

/** D-11's cap. Three chips is a row a thumb can read; a fourth is a menu. */
const MAX_SUGGESTED_REPLIES = 3;

/**
 * Narrow whatever the `done` frame claims its chips are into something safe to render.
 *
 * The server already trims, caps and dash-filters these labels (`suggest_replies`, plan 02), so this
 * is not a second content rule — it re-guards SHAPE, independently, because a frame is attacker-
 * adjacent input and an unbounded array on it would otherwise become an unbounded row of buttons
 * (T-39-39). A non-array, a member that is not a string and an empty label all resolve to "no chip"
 * rather than to a throw.
 */
const narrowSuggestedReplies = (candidate: unknown): string[] => {
  if (!Array.isArray(candidate)) return [];
  return candidate
    .filter((label): label is string => typeof label === "string" && label.trim().length > 0)
    .slice(0, MAX_SUGGESTED_REPLIES);
};

/**
 * Drive one wire frame into the panel.
 *
 * The string literals below are the wire's own kind names, spelled exactly as
 * `chat-agent/src/channels/webTransport.ts` writes them. Nine kinds, nine branches: the transport
 * already drops a frame whose kind it does not recognise, so a `default` here would be unreachable
 * for anything the server can actually send, and an exhaustive switch is what makes a tenth
 * server-side kind a visible gap rather than a silent no-op.
 */
const handleFrame = (frame: WireFrame): void => {
  runtime.lastFrameAt = Date.now();
  if (state.reconnecting) setState({ reconnecting: false });

  // The optimistic user bubble goes to full opacity on the first server frame of any kind: the
  // server has the message, which is the only thing the reduced opacity was ever claiming.
  if (runtime.pending !== null) {
    patchRow(runtime.pending.userRowId, { pending: false });
  }

  switch (frame.kind) {
    case 'delta': {
      if (!runtime.firstDeltaSeen) {
        runtime.firstDeltaSeen = true;
        runtime.deps?.track.responseStreamed({ ttfb_ms: Date.now() - runtime.turnStartedAt });
      }
      // The status line is REPLACED by the answer, never kept beside it (D-07).
      if (state.toolName !== null) setState({ toolName: null });
      appendDelta(frame.text);
      return;
    }
    case 'toolStart': {
      // One line at a time. A second tool replaces the first rather than stacking under it.
      setState({ toolName: frame.toolName });
      return;
    }
    case 'toolEnd': {
      setState({ toolName: null });
      return;
    }
    case 'degraded': {
      // Disclosed only because it arrived, which only happens when a deep tool was actually refused
      // (D-17). There is no always-on banner and this flag is not set anywhere else.
      runtime.degradedSeen = true;
      if (runtime.streamingRowId !== null) patchRow(runtime.streamingRowId, { degraded: true });
      return;
    }
    case 'done': {
      finaliseTurn({ content: frame.content, blocks: frame.blocks });
      // Set UNCONDITIONALLY, including to `[]` when the key is absent: a turn that offers no
      // follow-ups must clear whatever the previous turn left behind, even if the send-side clear
      // was somehow missed. Assigning only when present would be how a stale chip row survives.
      setState({ suggestedReplies: narrowSuggestedReplies(frame.suggestedReplies) });
      return;
    }
    case 'interrupted': {
      finaliseTurn({ content: frame.content, intent: 'interrupted' });
      return;
    }
    case 'limit': {
      // A refusal is NOT the bot talking, so it is a system row rather than a bubble.
      appendRow({
        kind: "system",
        id: localRowId("limit"),
        system: "limit",
        createdAt: nowIso(),
        layer: frame.layer,
        retryAfterSec: frame.retryAfterSec,
      });
      applyLimit({ layer: frame.layer, retryAfterSec: frame.retryAfterSec });
      return;
    }
    case 'rehydrate': {
      // NO COPY for this case. The buffer is gone (the dyno cycled, or the run finished while the
      // browser was away); the thread is re-read and rerendered. A message here would explain an
      // implementation detail to somebody who did not notice a problem.
      void loadThread();
      return;
    }
    case 'error': {
      // Held rather than rendered: the transport may still resume, and an error row that appears and
      // then disappears is worse than one that arrives late.
      // The frame's own `code` is a SERVER string (`run_failed`, …), not one of the client's twelve,
      // and the panel's Arabic copy is keyed on the client map. Collapsing it to `internal_error`
      // keeps one refusal to one translation rather than leaking an untranslated machine string.
      runtime.pendingErrorCode = chatClientErrorMap.internal_error;
      return;
    }
  }
};

/** Lock the composer with a countdown, and unlock it silently when the window passes. */
const applyLimit = ({
  layer,
  retryAfterSec,
}: {
  layer: "ip" | "session" | "user";
  retryAfterSec: number;
}): void => {
  const seconds = Number.isFinite(retryAfterSec) ? Math.max(0, Math.floor(retryAfterSec)) : 0;
  setState({ limit: { layer, retryAfterSec: seconds }, countdownSec: seconds });
  runtime.deps?.track.limitReached({
    layer,
    authenticated: (runtime.deps?.jwt ?? null) !== null,
  });

  const tick = (): void => {
    const remaining = state.countdownSec - 1;
    if (remaining <= 0) {
      // Silently. Nobody needs to be told they have been un-punished.
      setState({ limit: null, countdownSec: 0 });
      return;
    }
    setState({ countdownSec: remaining });
    const next = setTimeout(() => {
      runtime.timers.delete(next);
      tick();
    }, COUNTDOWN_INTERVAL_MS);
    registerTimer(next);
  };

  if (seconds > 0) {
    const first = setTimeout(() => {
      runtime.timers.delete(first);
      tick();
    }, COUNTDOWN_INTERVAL_MS);
    registerTimer(first);
  }
};

/**
 * The reconnect loop. WHEN TO RETRY, which is a different question from when to tell the visitor.
 *
 * The backoff is the transport's own — 500ms doubling to an 8000ms ceiling over a bounded budget,
 * imported rather than restated so the two cannot drift. `openStream` is driven directly instead of
 * `streamTurnWithResume` for one reason: the resume helper does not report which attempt it is on,
 * and D-09's visibility rule needs exactly that. The helper's OTHER recovery (a dead session) is
 * reproduced here in the same shape and outside the reconnect budget, because a stale credential is
 * a different failure class from a dropped socket.
 */
const runStream = async ({
  sessionId,
  messageId,
  text,
  pageContext,
}: {
  sessionId: string;
  messageId: string;
  text: string;
  pageContext: PageContext;
}): Promise<void> => {
  const controller = new AbortController();
  runtime.controller = controller;

  let activeSession = sessionId;
  let cursor: number | null = null;
  let mintedOnce = false;
  let retries = 0;
  let delay = RECONNECT_INITIAL_DELAY_MS;

  for (;;) {
    const result = await openStream({
      sessionId: activeSession,
      jwt: runtime.deps?.jwt ?? null,
      messageId,
      text,
      pageContext,
      lastEventId: cursor,
      onFrame: handleFrame,
      onId: (id) => {
        cursor = id;
      },
      signal: controller.signal,
    });

    if (!isChatClientFailure(result)) {
      if (runtime.attempt > 1 && result.data.framesDelivered > 0) {
        // A resumed stream that delivered frames IS a successful resume. The count is every frame
        // that attempt carried, replayed and live alike: the wire does not mark the boundary between
        // the buffer's tail and the model's next token, and inventing one would be a guess.
        runtime.deps?.track.streamReconnected({ frames_replayed: result.data.framesDelivered });
      }
      if (result.data.terminal !== null) return;
      // A stream that ended with NO terminal frame is a drop, not an answer.
    } else if (result.code === chatClientErrorMap.aborted) {
      return;
    } else if (
      !mintedOnce &&
      (result.code === chatClientErrorMap.session_expired ||
        result.code === chatClientErrorMap.session_unknown ||
        result.code === chatClientErrorMap.session_required)
    ) {
      mintedOnce = true;
      clearStoredSession();
      setState({ sessionId: null, conversationId: null });
      const renewed = await ensureSession();
      if (renewed === null) {
        runtime.pendingErrorCode = chatClientErrorMap.network_error;
        return;
      }
      activeSession = renewed;
      // A new session is a new conversation, so the old cursor belongs to a stranger's buffer.
      cursor = null;
      continue;
    } else if (result.code !== chatClientErrorMap.network_error) {
      // Every refusal is an ANSWER: `engine_not_streaming`, `rate_limited`, `invalid_request`. None
      // of them gets better by being asked again.
      if (result.code === chatClientErrorMap.rate_limited && result.limit !== undefined) {
        appendRow({
          kind: "system",
          id: localRowId("limit"),
          system: "limit",
          createdAt: nowIso(),
          layer: result.limit.layer,
          retryAfterSec: result.limit.retryAfterSec,
        });
        applyLimit(result.limit);
        return;
      }
      runtime.pendingErrorCode = result.code;
      return;
    }

    if (controller.signal.aborted) return;

    retries += 1;
    if (retries >= RECONNECT_MAX_ATTEMPTS) {
      runtime.pendingErrorCode = chatClientErrorMap.reconnect_exhausted;
      return;
    }

    runtime.attempt = retries + 1;
    scheduleReconnectWatch();
    await new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        runtime.timers.delete(timer);
        resolve();
      }, delay);
      registerTimer(timer);
    });
    delay = Math.min(delay * 2, RECONNECT_MAX_DELAY_MS);
  }
};

/** Everything that must be true again once a turn is over, however it ended. */
const endTurn = (): void => {
  const errorCode = runtime.pendingErrorCode;
  const unfinished =
    runtime.streamingRowId !== null &&
    state.rows.some(
      (row) => row.kind === "turn" && row.id === runtime.streamingRowId && row.streaming === true
    );

  if (unfinished && runtime.streamingRowId !== null) {
    // The server never sent a terminal frame. Whatever text arrived is what there is; stranding it
    // as a permanently "streaming" bubble would leave a caret blinking forever.
    patchRow(runtime.streamingRowId, { streaming: false });
  }

  if (errorCode !== null) {
    appendRow({
      kind: "system",
      id: localRowId("error"),
      system: runtime.firstDeltaSeen ? "error" : "send_failed",
      createdAt: nowIso(),
      code: errorCode,
    });
  } else {
    // A turn that produced an answer is a completed send, so the next composed message gets a new
    // key. A FAILED send deliberately does not retire its key: the retry is the same message.
    messageKeys.completeSend();
    runtime.pending = null;
  }

  runtime.controller = null;
  runtime.streamingRowId = null;
  runtime.firstDeltaSeen = false;
  runtime.degradedSeen = false;
  runtime.pendingErrorCode = null;
  runtime.attempt = 1;
  clearRuntimeTimers();
  setState({
    streaming: false,
    toolName: null,
    reconnecting: false,
    stopPending: false,
    // The chip row must not hang under a partial answer, so a turn that ENDED BADLY clears it.
    //
    // THE CONDITION IS LOAD-BEARING AND MUST NOT BE SIMPLIFIED AWAY. `endTurn` runs in
    // `sendMessage`'s `finally`, which is AFTER `handleFrame`'s `done` arm has already set the
    // chips for this turn, so an unconditional `suggestedReplies: []` here would silently wipe
    // every chip row a fraction of a second after it arrived and the feature would never render
    // once. Only a turn with no clean terminal frame — an error, or a stream that stopped without
    // one — gets cleared; a `done` turn keeps what `done` gave it, and any turn that failed before
    // `done` already has `[]` from the send-side clear.
    ...(errorCode !== null || unfinished ? { suggestedReplies: [] } : {}),
  });
};

/**
 * Send a composed message.
 *
 * `keyForSend()` on EVERY attempt: the registry, not this handler, owns the rule that the key is
 * stable across retries of one composed message and fresh for a genuinely new one. That stability is
 * what makes a double-tap produce one turn, and it is covered by `chatTurnPolicy`'s suite rather than
 * by a walkthrough.
 */
const sendMessage = async ({
  text,
  pageContext,
  chipId,
  chipPosition,
}: {
  text: string;
  pageContext: PageContext;
  chipId?: string;
  chipPosition?: number;
}): Promise<void> => {
  const body = typeof text === "string" ? text.trim() : "";
  if (body.length === 0) return;
  if (state.streaming || state.limit !== null) return;

  const messageId = messageKeys.keyForSend();
  const userRowId = runtime.pending?.userRowId ?? localRowId("user");

  if (runtime.pending === null) {
    appendRow({
      kind: "turn",
      id: userRowId,
      role: "user",
      content: body,
      createdAt: nowIso(),
      pending: true,
    });
  } else {
    patchRow(userRowId, { pending: true });
  }
  runtime.pending = { text: body, pageContext, userRowId };

  runtime.turnStartedAt = Date.now();
  runtime.lastFrameAt = runtime.turnStartedAt;
  runtime.attempt = 1;
  runtime.threadEpoch += 1;
  // D-11 exactly: the chips clear the moment the visitor sends ANYTHING, tapped or typed, which is
  // how Messenger quick replies already behave. Clearing here rather than in the chip component is
  // what makes a typed message clear them too.
  setState({
    streaming: true,
    suggestedReplies: [],
    pinToBottomToken: state.pinToBottomToken + 1,
  });

  if (chipId !== undefined) {
    // `chip_id` says WHICH KIND of chip and `position` says which one of the row; together they are
    // a funnel key that survives any copy change, because there is no copy in it to change. That
    // matters most for the MODEL chips, which have no stable id by construction and therefore pass
    // the fixed synthetic `model_suggested` plus their index. THE ARABIC LABEL IS NEVER PASSED HERE
    // and no helper on `trackChat` has a parameter that could carry one.
    runtime.deps?.track.suggestionClicked({
      chip_id: chipId,
      route_type: pageContext.routeType,
      ...(chipPosition === undefined ? {} : { position: chipPosition }),
    });
  }
  runtime.deps?.track.messageSent({
    route_type: pageContext.routeType,
    store_slug: pageContext.storeSlug,
    category_slug: pageContext.categorySlug,
    authenticated: (runtime.deps?.jwt ?? null) !== null,
  });

  try {
    const sessionId = await ensureSession();
    if (sessionId === null) {
      runtime.pendingErrorCode = chatClientErrorMap.network_error;
      return;
    }
    await runStream({ sessionId, messageId, text: body, pageContext });
  } catch {
    // The transport never throws, so reaching here means a defect somewhere above. It still must not
    // leave the composer locked forever.
    runtime.pendingErrorCode = chatClientErrorMap.internal_error;
  } finally {
    endTurn();
  }
};

/** Re-issue the failed send. The key is unchanged, which is the whole point of the registry. */
const retrySend = (): void => {
  const previous = runtime.pending;
  if (previous === null || state.streaming) return;
  setState({
    rows: state.rows.filter((row) => row.kind !== "system" || row.system !== "send_failed"),
  });
  void sendMessage({ text: previous.text, pageContext: previous.pageContext });
};

/**
 * WEB-07's stop, and the honesty is the design.
 *
 * There is NO optimistic transition to interrupted. Aborting the local reader does not stop the run
 * (the dyno keeps paying for tokens nobody is listening to), so the stop is a POST, and claiming the
 * turn stopped while the server is still spending would be a lie — one that would also strand a real
 * `done` frame arriving afterwards against an already-terminal local state. Only the server's own
 * terminal frame finalises the turn.
 */
const stopGeneration = async (): Promise<void> => {
  if (!state.streaming || state.stopPending) return;
  const sessionId = state.sessionId ?? readStoredSessionId();
  if (sessionId === null) return;

  setState({ stopPending: true });
  runtime.deps?.track.generationStopped({ partial_chars: currentPartial().length });

  const result = await requestAbort({ sessionId, jwt: runtime.deps?.jwt ?? null });
  setState({ stopPending: false });

  if (isChatClientFailure(result)) {
    // The button returns to enabled, the turn keeps streaming to its natural end, and the composer
    // STAYS locked, because the bot is in fact still talking. A second press retries the abort.
    appendRow({
      kind: "system",
      id: localRowId("stop"),
      system: "stop_failed",
      createdAt: nowIso(),
      code: result.code,
    });
  }
  // `aborted: false` means the run had already finished. Not an error, and not a reason to stop
  // waiting: the terminal frame is already on its way.
};

/** D-18's nudge. The modal itself is captured centrally by `showAuthModal`. */
const noteLoginNudgeClicked = (layer: "ip" | "session" | "user"): void => {
  runtime.deps?.track.loginNudgeClicked({ layer });
};

/**
 * A store card tap inside a `stores` carousel (WEB-03).
 *
 * The capture lives HERE rather than in `ChatStoreCarousel`, per CLAUDE.md's shared-primitive rule
 * and this panel's own rule that every capture is one of this file's handlers. The component's job
 * is to hand over a slug, a position and the size of the row it was in; deciding what that becomes
 * in PostHog, and attaching the route the visitor was on when they tapped, is this file's.
 *
 * `route_type` comes off `runtime.deps` for the same reason every other handler here reads it there:
 * these actions are module-level so a remount cannot kill them, which means they cannot call hooks.
 */
const noteStoreCardClicked = ({
  store_slug,
  position,
  card_count,
}: {
  store_slug: string;
  position: number;
  card_count: number;
}): void => {
  runtime.deps?.track.storeCardClicked({
    store_slug,
    position,
    card_count,
    route_type: runtime.deps.routeType,
  });
};

/**
 * A contact icon tap on a card (39-08), same module-level shape as `noteStoreCardClicked` above
 * and for the same remount-survival reason. `contact_kind` is a closed literal set; the tapped
 * destination is never a parameter — a phone number is PII-adjacent, and a maps query embeds the
 * store's Arabic name, which is prose.
 */
const noteStoreContactClicked = ({
  store_slug,
  contact_kind,
  position,
}: {
  store_slug: string;
  contact_kind: "phone" | "whatsapp" | "maps";
  position: number;
}): void => {
  runtime.deps?.track.storeContactClicked({
    store_slug,
    contact_kind,
    position,
    route_type: runtime.deps.routeType,
  });
};

/**
 * Clear every timer this runtime owns and abort the live reader.
 *
 * Called by the D-12 logout reset and exposed for tests. NOT called from the provider's unmount: a
 * Vike navigation that remounts the layout would then kill an in-flight stream, which is the failure
 * D-13 and D-14 rule out by contemplating a mid-conversation navigation in the first place.
 */
function teardownChatRuntime(): void {
  clearRuntimeTimers();
  if (runtime.controller !== null) {
    runtime.controller.abort();
    runtime.controller = null;
  }
  runtime.streamingRowId = null;
  runtime.pending = null;
  runtime.pendingErrorCode = null;
  runtime.firstDeltaSeen = false;
  runtime.degradedSeen = false;
  runtime.attempt = 1;
  runtime.threadEpoch += 1;
  messageKeys.completeSend();
}

// ─── React surface ────────────────────────────────────────────────────────────

export type ChatContextValue = {
  open: boolean;
  openPanel: () => void;
  closePanel: () => void;
  rows: ChatRow[];
  threadLoading: boolean;
  /** `null` means render NOTHING, per D-04's gate. Never a count. */
  unreadDot: boolean | null;
  routeType: ChatRouteType;
  pageContext: PageContext;
  conversationId: string | null;

  // ─── The turn in flight ───
  streaming: boolean;
  /** The tool name for `toolLabels.ts`'s Arabic lookup, or `null` for no status line. */
  toolName: string | null;
  reconnecting: boolean;
  stopPending: boolean;
  limit: ChatState["limit"];
  countdownSec: number;
  /** True while the composer must not accept input: a run is live, or a refusal is in force. */
  composerLocked: boolean;
  /** Changes on the visitor's own send. The list pins to the bottom when it does. */
  pinToBottomToken: number;
  /**
   * The model's follow-up chips for the latest assistant turn, or `[]`. Turn-scoped and never
   * persisted (D-09/D-11), so a rehydrated thread hands back an empty array.
   */
  suggestedReplies: string[];
  /**
   * `chipPosition` is declared at THREE sites in this file — here, on `defaultChatContext`, and on
   * the provider's `send` `useCallback` — and each declares the input shape independently rather
   * than deriving it, so a field added to two of the three compiles cleanly while never reaching
   * `sendMessage`. Widen all three or none.
   */
  sendMessage: (input: {
    text: string;
    pageContext?: PageContext;
    chipId?: string;
    chipPosition?: number;
  }) => void;
  stopGeneration: () => void;
  retrySend: () => void;
  noteLoginNudgeClicked: (layer: "ip" | "session" | "user") => void;
  /** A store card tap in a `stores` carousel. Slugs and counts only, never the card's title. */
  noteStoreCardClicked: (input: {
    store_slug: string;
    position: number;
    card_count: number;
  }) => void;
  /** A card contact icon tap (39-08). A closed kind literal, never the tapped destination. */
  noteStoreContactClicked: (input: {
    store_slug: string;
    contact_kind: "phone" | "whatsapp" | "maps";
    position: number;
  }) => void;
};

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

/** The server has no panel, so a server read of this context is a no-op rather than a throw. */
const defaultChatContext: ChatContextValue = {
  open: false,
  openPanel: () => {},
  closePanel: () => {},
  rows: [],
  threadLoading: false,
  unreadDot: null,
  routeType: "generic",
  pageContext: { routeType: "generic" },
  conversationId: null,
  streaming: false,
  toolName: null,
  reconnecting: false,
  stopPending: false,
  limit: null,
  countdownSec: 0,
  composerLocked: false,
  pinToBottomToken: 0,
  suggestedReplies: [],
  // Declaration site 2 of 3 for `chipPosition`. The SSR / no-provider no-op still has to declare the
  // same input shape, or the object stops satisfying `ChatContextValue`.
  sendMessage: (_input: {
    text: string;
    pageContext?: PageContext;
    chipId?: string;
    chipPosition?: number;
  }) => {},
  stopGeneration: () => {},
  retrySend: () => {},
  noteLoginNudgeClicked: () => {},
  noteStoreCardClicked: () => {},
  noteStoreContactClicked: () => {},
};

export const useChat = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    if (typeof window === "undefined") return defaultChatContext;
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
};

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const pageContextValue = usePageContext();
  const pathname = pageContextValue.urlPathname || "";
  const { session } = useAuth();
  const { trackChat } = usePostHogTracking();

  // THE JWT SOURCE. In-memory React state, and the only one this file reads: the auth context is the
  // single source of truth for who is logged in right now, and adding a storage read here would make
  // a two-key inconsistency into a three-key one.
  const jwt = session?.token ?? null;

  const routeType = useMemo(() => resolveRouteType(pathname), [pathname]);
  const pageContext = useMemo(() => resolvePageContext(pathname), [pathname]);

  // Keep the runtime's view of the world current. The runtime's actions are module-level (so a
  // remount cannot kill them) and therefore cannot call hooks; this is how they see the JWT and the
  // route. An effect rather than a render-time assignment, because a side effect during render is
  // exactly the thing React's double-invocation is designed to expose.
  useEffect(() => {
    runtime.deps = { track: trackChat, jwt, routeType, pathname };
  }, [trackChat, jwt, routeType, pathname]);

  // Page-load bootstrap: resolve the last-seen mark, then ask for the count. Both inputs feed
  // `resolveUnreadDot`, which renders nothing until both have landed.
  const bootstrapped = useRef(false);
  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    runtime.deps = { track: trackChat, jwt, routeType, pathname };
    runtime.lastSeen = readLastSeen();
    setState({ lastSeenResolved: true, sessionId: readStoredSessionId() });
    void refreshUnread();
    // Deliberately once per page load, not per navigation: D-04 is a page-load check, and re-running
    // it on every route change would announce nothing new while costing a request each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // D-11 / D-12, decided by the policy module rather than by an `if` ladder grown in place.
  const previousToken = useRef<string | null>(null);
  useEffect(() => {
    const verdict = resolveIdentityTransition({
      previousToken: previousToken.current,
      nextToken: jwt,
    });
    previousToken.current = jwt;

    if (verdict === identityTransitionMap.attribute) {
      void attributeIdentity();
      return;
    }
    if (verdict === identityTransitionMap.reset) {
      resetIdentity();
    }
  }, [jwt]);

  // quick-261021-jzd: page visibility for the thread poll. Read in an effect, never at render, so SSR
  // and hydration agree.
  const [visible, setVisible] = useState<boolean>(() =>
    typeof document !== "undefined" ? document.visibilityState !== "hidden" : true
  );
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // The open panel's poll. Keyed on the delay alone, so it stops on close, hide, stream start and
  // unmount, and switches cadence when the mode flips (Release drops back to the 20s probe).
  const pollDelayMs = resolveThreadPollDelayMs({
    open: snapshot.open,
    humanMode: snapshot.humanMode,
    streaming: snapshot.streaming,
    visible,
  });
  useEffect(() => {
    if (pollDelayMs === null) return undefined;
    const timer = setInterval(() => void pollThread(), pollDelayMs);
    return () => clearInterval(timer);
  }, [pollDelayMs]);

  // Coming back to a hidden tab with the panel open: catch up at once rather than waiting a whole
  // interval. Opening the panel needs no call here, `openPanel` already rehydrates.
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current && getSnapshot().open) void pollThread();
    wasVisible.current = visible;
  }, [visible]);

  // The provider's own cleanup clears what the PROVIDER owns and nothing else. It deliberately does
  // not tear the runtime down: a Vike navigation that remounts the layout would then kill an
  // in-flight stream, which is the failure D-13/D-14 rule out.
  useEffect(
    () => () => {
      runtime.deps = null;
    },
    []
  );

  // The page context is captured PER SEND, not per session (D-13/D-14): a visitor who navigates
  // mid-conversation must have the next message carry the new page, and the stream that is already
  // running must not be disturbed by it.
  //
  // Declaration site 3 of 3 for `chipPosition`, and the ONE that actually loses a value when it is
  // forgotten: this callback re-declares the input type AND hand-builds the forwarded object, and an
  // explicit spread copies only the properties it names. A widened type with an unwidened spread
  // compiles, passes every grep for the field name, and drops the value on the floor.
  const send = useCallback(
    (input: {
      text: string;
      pageContext?: PageContext;
      chipId?: string;
      chipPosition?: number;
    }) => {
      void sendMessage({
        text: input.text,
        pageContext: input.pageContext ?? pageContext,
        ...(input.chipId === undefined ? {} : { chipId: input.chipId }),
        ...(input.chipPosition === undefined ? {} : { chipPosition: input.chipPosition }),
      });
    },
    [pageContext]
  );

  const stop = useCallback(() => {
    void stopGeneration();
  }, []);

  const value = useMemo<ChatContextValue>(
    () => ({
      open: snapshot.open,
      openPanel,
      closePanel,
      rows: snapshot.rows,
      threadLoading: snapshot.threadLoading,
      unreadDot: resolveUnreadDot({
        unreadCount: snapshot.unreadCount,
        lastSeenResolved: snapshot.lastSeenResolved,
      }),
      routeType,
      pageContext,
      conversationId: snapshot.conversationId,
      streaming: snapshot.streaming,
      toolName: snapshot.toolName,
      reconnecting: snapshot.reconnecting,
      stopPending: snapshot.stopPending,
      limit: snapshot.limit,
      countdownSec: snapshot.countdownSec,
      composerLocked: snapshot.streaming || snapshot.limit !== null,
      pinToBottomToken: snapshot.pinToBottomToken,
      suggestedReplies: snapshot.suggestedReplies,
      sendMessage: send,
      stopGeneration: stop,
      retrySend,
      noteLoginNudgeClicked,
      noteStoreCardClicked,
      noteStoreContactClicked,
    }),
    [snapshot, routeType, pageContext, send, stop]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatProvider;
